import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { UpdateModuleUserPinnedStatus } from 'app/library/module-user/actions';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleCategory } from 'app/library/module/enums/moduleCategory';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TenantDataService } from '../../../../config/services/tenant-data.service';
import { Feature } from '../../../feature/models/feature.model';
import { FeatureState } from '../../../feature/models/feature.state';
import { FinalGrade } from '../../../final-grade/models/final-grade.model';
import { Module } from '../../../module/models/module.model';
import { TagMultiSelectService } from '../../../tag/services/multi-select.service';
import { MyModulesState } from '../../models/my-modules.state';

@Component({
    selector: 'app-my-modules-list',
    templateUrl: './my-modules-list.component.html',
    styleUrls: ['./my-modules-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class MyModulesListComponent extends UnsubscribeOnDestroy implements OnInit {
    @Select(FeatureState.getEntities)
    feature$: Observable<Feature[]>;
    features: Feature[];

    @Select(UserState.getMyInfo)
    profile$: Observable<User>;
    profile: User;

    @Select(MyModulesState.getEntities)
    myModuleUser$: Observable<ModuleUser[]>;
    moduleUser: ModuleUser;

    @Input()
    isTeamMembersPage: boolean;

    @Input()
    myModule$: Observable<Module[]>;

    @Input()
    isPinned: boolean;

    @Input()
    showPinModule: boolean;

    @Input()
    listType: ModuleCategory;

    /** Fixed 380px tiles that only add/remove columns (My Courses). */
    @Input()
    fixedWidthTiles = false;

    @Input()
    finalGrades: {
        [key: string]: FinalGrade;
    };

    @Input()
    myModuleUser: {
        [key: string]: ModuleUser;
    };

    isCertificateOfAttendanceFeatureEnabled: boolean;

    constructor(
        private _tenantDataService: TenantDataService,
        private _featureSwitchPipe: FeatureSwitchPipe,
        public tagMultiSelectService: TagMultiSelectService,
        private _store: Store
    ) {
        super();

        this.isCertificateOfAttendanceFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CertificateOfAttendance);
    }

    ngOnInit(): void {
        this.profile$
            .pipe(
                filter((profile) => !!profile),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((profile) => {
                this.profile = profile;
            });
    }

    onPinModule(event: { moduleId: string; isPinned: boolean }): void {
        this._store.dispatch(new UpdateModuleUserPinnedStatus({ moduleId: event.moduleId, isPinned: event.isPinned, listType: this.listType }));
    }

    private canAddNewModule(): 'yes' | 'no' | 'private' | 'basic' {
        if (
            this.features &&
            this.features.filter((feature) => feature.id === 'module_visibility' && feature.status === 'inactive').length === 1 &&
            this.features.filter((feature) => feature.id === 'e_learning' && feature.status === 'inactive').length === 1 &&
            this.profile &&
            this.profile.permissions.indexOf(AdminPermissions.TENANT_MANAGEMENT) !== -1
        ) {
            return 'basic';
        }

        if (this.profile && this.profile.permissions.indexOf(AdminPermissions.TENANT_MANAGEMENT) !== -1) {
            return 'yes';
        }

        if (
            this.features &&
            this.features.filter((feature) => feature.id === 'module_visibility' && feature.status === 'active').length === 1 &&
            this._tenantDataService.data.canCreatePrivateModule
        ) {
            return 'private';
        }

        return 'no';
    }

    get getAddNewModuleLabel(): string {
        switch (this.canAddNewModule()) {
            case 'yes':
            case 'basic':
                return 'PAGE.ALL_MODULES.BUTTON.ADD';
            case 'private':
                return 'PAGE.ALL_MODULES.BUTTON.ADD_PRIVATE';
            default:
                return null;
        }
    }

    get getAddNewModuleRoute(): any[] {
        switch (this.canAddNewModule()) {
            case 'yes':
                return ['', { outlets: { modal: 'new-module' } }];
            case 'private':
                return ['', { outlets: { modal: 'new-module/private' } }];
            case 'basic':
                return ['', { outlets: { modal: 'new-module/basic' } }];
            default:
                return null;
        }
    }
}
